import { useMemo } from 'react';
import {
  Select as MuiSelect,
  FormHelperText,
  FormLabel,
  MenuItem,
  FormControl,
  Typography,
  FormControlProps,
  SxProps,
  Box,
  IconButton,
} from '@mui/material';
import themes from '~/themes';
import { BaseSelectProps as _SelectProps } from '@mui/material/Select';
import { KeyboardArrowDown } from '@mui/icons-material';
import CloseIcon from '~/assets/images/icons/close.svg';
import { orderBy } from 'lodash';

type SelectOptionProps = {
  label?: string | any;
  value?: string | any;
};

export interface SelectProps extends _SelectProps {
  legend?: React.ReactNode | string;
  value?: any;
  options: Array<SelectOptionProps>;
  helperText?: string;
  error?: boolean;
  fullWidth?: boolean;
  noOptionsText?: string;
  placeholder?: string;
  sxPlaceholder?: SxProps;
  popupWidthFixed?: number;
  paperMaxHeight?: number;
  renderValue?: (value: any) => React.ReactNode;
  renderOption?: (value: any) => React.ReactNode;
  formControlProps?: FormControlProps;
  onClear?: () => void;
  sortOption?: boolean;
  sxLabel?: SxProps;
}

export const Select: React.FC<SelectProps> = ({
  label,
  legend,
  value,
  children,
  options,
  error = false,
  helperText,
  fullWidth = true,
  noOptionsText,
  placeholder,
  popupWidthFixed,
  formControlProps,
  paperMaxHeight = 500,
  id,
  sxPlaceholder,
  renderValue,
  renderOption,
  onClear,
  sortOption = false,
  sxLabel,
  ...rest
}) => {
  const sortedOptions = useMemo(() => {
    if (!options?.length) return [];
    if (sortOption) {
      return orderBy(options, [(o) => o?.label?.toLowerCase()], ['asc']);
    }
    return options;
  }, [options, sortOption]);

  const renderOptions = () => {
    if (!sortedOptions.length)
      return (
        <Typography color={themes.color.gray600} data-testid={`ddl-no-${id}`}>
          {noOptionsText || 'No options'}
        </Typography>
      );
    return sortedOptions.map((option: SelectOptionProps) => {
      if (renderOption) {
        return renderOption(option);
      }
      return (
        <MenuItem
          key={option?.value}
          value={option?.value || ''}
          data-testid={`ddl-${id}`}
          disableRipple
          sx={{
            'color': themes.color.black,
            'whiteSpace': 'normal !important',
            'textOverflow': 'ellipsis',
            'overflow': 'hidden',
            'borderRadius': '15px',
            '&.MuiMenuItem-gutters': {
              '&:hover': {
                background: `${themes.bg.gray300}`,
              },
            },
            '&.Mui-selected': {
              'backgroundColor': themes.bg.cloudGray,
              '&:focus': {
                backgroundColor: themes.bg.cloudGray,
              },
            },
          }}
        >
          {option?.label}
        </MenuItem>
      );
    });
  };

  if (onClear)
    return (
      <FormControl fullWidth={fullWidth} {...formControlProps}>
        {legend && (
          <FormLabel sx={sxLabel}>
            {typeof legend === 'string' ? legend : legend}
          </FormLabel>
        )}
        <Box sx={{ position: 'relative', width: '100%' }}>
          <MuiSelect
            fullWidth
            {...rest}
            value={value || ''}
            data-value={value ? `${value}-select` : ''}
            data-testid={`${id}-select`}
            error={error}
            displayEmpty
            IconComponent={(props) =>
              value && onClear ? null : (
                <KeyboardArrowDown
                  {...props}
                  sx={{ color: `${themes.color.violet900} !important` }}
                />
              )
            }
            renderValue={
              renderValue ||
              ((val) => {
                if (!value && placeholder) {
                  return (
                    <Typography
                      color={themes.color.black}
                      sx={{ lineHeight: 1, ...sxPlaceholder }}
                    >
                      {placeholder}
                    </Typography>
                  );
                }
                const l = options.find((o) => o.value === val)?.label || '';
                return l;
              })
            }
            MenuProps={{
              MenuListProps: {
                style: {
                  padding: '10px',
                  ...(popupWidthFixed && {
                    maxWidth: popupWidthFixed,
                    minWidth: popupWidthFixed,
                  }),
                },
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              PaperProps: {
                className: 'customized-scrollbar',
                style: {
                  boxShadow: '0px 1px 6px #160E4D14',
                  borderRadius: '15px',
                  opacity: '1',
                  maxHeight: paperMaxHeight || 'calc(100% - 96px)',
                },
              },
            }}
          >
            {children || renderOptions()}
          </MuiSelect>
          {value && onClear && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              size='small'
              sx={{
                position: 'absolute',
                right: '9px',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                color: themes.color.black,
                zIndex: 2,
              }}
            >
              <img alt='search-icon' src={CloseIcon} width={10} height={11} />
            </IconButton>
          )}
          {helperText ? (
            <FormHelperText
              data-testid={`${id}-select-error`}
              error={error}
              sx={{ marginLeft: 0, marginRight: 0 }}
            >
              {helperText}
            </FormHelperText>
          ) : null}
        </Box>
      </FormControl>
    );

  return (
    <FormControl fullWidth={fullWidth} {...formControlProps}>
      {legend && (
        <FormLabel sx={sxLabel}>
          {typeof legend === 'string' ? legend : legend}
        </FormLabel>
      )}
      <MuiSelect
        {...rest}
        value={value || ''}
        data-value={value ? `${value}-select` : ''}
        data-testid={`ddl-${id}`}
        error={error}
        displayEmpty
        IconComponent={(props) => (
          <KeyboardArrowDown
            {...props}
            sx={{ color: `${themes.color.violet900} !important` }}
          />
        )}
        renderValue={
          renderValue ||
          ((val) => {
            if (!value && placeholder) {
              return (
                <Typography
                  color={themes.color.black}
                  sx={{ lineHeight: 1, ...sxPlaceholder }}
                >
                  {placeholder}
                </Typography>
              );
            }
            const l = options.find((o) => o.value === val)?.label || '';
            return l;
          })
        }
        MenuProps={{
          MenuListProps: {
            style: {
              padding: '10px',
              ...(popupWidthFixed && {
                maxWidth: popupWidthFixed,
                minWidth: popupWidthFixed,
              }),
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          PaperProps: {
            className: 'customized-scrollbar',
            style: {
              boxShadow: '0px 1px 6px #160E4D14',
              borderRadius: '15px',
              opacity: '1',
              maxHeight: paperMaxHeight || 'calc(100% - 96px)',
            },
          },
        }}
      >
        {children || renderOptions()}
      </MuiSelect>
      {helperText ? (
        <FormHelperText
          data-testid={`ddl-error-${id}`}
          error={error}
          sx={{ marginLeft: 0, marginRight: 0 }}
        >
          {helperText}
        </FormHelperText>
      ) : null}
    </FormControl>
  );
};
